import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private userSubject = new BehaviorSubject<any>(null); // Initialize with null or default value
  public user$ = this.userSubject.asObservable();

  // Observable for filtered changes - array where each group is its own indexed key
  private filteredChangesSubject = new BehaviorSubject<Array<{ groupName: string; changes: Array<{ summary: string; meta?: string }> }>>([]);
  public filteredChanges$ = this.filteredChangesSubject.asObservable();

  constructor(public _http: HttpClient) { }

  private normalizeGroupKey(name: any): string {
    return (name ?? '').toString().trim().toLowerCase();
  }

  private dedupeChanges(changes: Array<{ summary: string; meta?: string }>): Array<{ summary: string; meta?: string }> {
    if (!Array.isArray(changes) || changes.length === 0) {
      return [];
    }
    const seen = new Set<string>();
    const out: Array<{ summary: string; meta?: string }> = [];
    for (const c of changes) {
      if (!c || !c.summary) continue;
      const key = `${(c.summary ?? '').toString().trim()}|${(c.meta ?? '').toString().trim()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }

  /**
   * Sets the User Observable to the data passed in
   * @param data JSON object that is information about user, retrieved from database.
   */
  setUserObservable(data: any) {
    this.userSubject.next(data);
  }

  /**
   * Call to MSFT graph to get signed in users data
   * @returns JSON object that is information about user
   */
  graphMeCall() {
    return this._http.get<any>('https://graph.microsoft.com/v1.0/me');
  }

  /**
   * Update or add changes for a specific group
   * @param groupIndex The index of the group (0, 1, 2, ...)
   * @param groupName The name of the group
   * @param changes Array of changes for this group
   */
  updateFilteredChanges(groupIndex: number, groupName: string, changes: Array<{ summary: string; meta?: string }>) {
    const currentChanges = this.filteredChangesSubject.value;

    const normalized = this.normalizeGroupKey(groupName);
    const nextEntry = { groupName, changes: this.dedupeChanges(changes) };

    // Treat groupIndex as a hint only. Resolve the actual slot by groupName to
    // avoid clobbering other groups when callers have stale indices.
    let targetIndex = groupIndex;
    if (normalized) {
      const existingIndex = currentChanges.findIndex(g => this.normalizeGroupKey(g?.groupName) === normalized);
      if (existingIndex >= 0) {
        targetIndex = existingIndex;
      } else {
        const indexEntry = currentChanges[targetIndex];
        const indexKey = this.normalizeGroupKey(indexEntry?.groupName);
        const canUseHint = !indexKey || indexKey === normalized;
        if (!canUseHint) {
          targetIndex = currentChanges.length;
        }
      }
    }

    // Ensure array has enough elements
    while (currentChanges.length <= targetIndex) {
      currentChanges.push({ groupName: '', changes: [] });
    }

    // Update the resolved target index
    currentChanges[targetIndex] = nextEntry;

    // Clear any other entries that refer to the same group (case-insensitive)
    // This prevents stale duplicates from lingering after edits/removals.
    if (normalized) {
      for (let i = 0; i < currentChanges.length; i++) {
        if (i === targetIndex) continue;
        const k = this.normalizeGroupKey(currentChanges[i]?.groupName);
        if (k && k === normalized) {
          currentChanges[i] = { groupName: '', changes: [] };
        }
      }
    }

    this.filteredChangesSubject.next([...currentChanges]);
  }

  /**
   * Get all filtered changes
   * @returns Current value of filtered changes
   */
  getFilteredChanges() {
    return this.filteredChangesSubject.value;
  }

  /**
   * Clear all filtered changes
   */
  clearFilteredChanges() {
    this.filteredChangesSubject.next([]);
  }

  /**
   * Remove changes for a specific group
   * @param groupIndex The index of the group to remove
   */
  removeFilteredChanges(groupIndex: number) {
    const currentChanges = this.filteredChangesSubject.value;
    if (groupIndex < currentChanges.length) {
      currentChanges[groupIndex] = { groupName: '', changes: [] };
      this.filteredChangesSubject.next([...currentChanges]);
    }
  }

  /**
   * Load filtered changes from cliConfig data
   * @param cliConfig Array of group changes to load
   */
  loadFilteredChanges(cliConfig: Array<{ groupName: string; changes: Array<{ summary: string; meta?: string }> }>) {
    const groups = Array.isArray(cliConfig) ? cliConfig : [];
    const merged = new Map<string, { groupName: string; changes: Array<{ summary: string; meta?: string }> }>();

    for (const g of groups) {
      const displayName = (g?.groupName ?? '').toString().trim();
      const key = this.normalizeGroupKey(displayName);
      if (!key) continue;
      const incoming = this.dedupeChanges(Array.isArray(g?.changes) ? g.changes : []);
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, { groupName: displayName, changes: incoming });
      } else {
        // Prefer existing display casing; merge + dedupe changes
        existing.changes = this.dedupeChanges(existing.changes.concat(incoming));
      }
    }

    this.filteredChangesSubject.next(Array.from(merged.values()));
  }
}